import { useAction, useAtom } from '@reatom/npm-react'
import { useMemo } from 'react'
import { styled } from 'styled-components'

import { Array, AsyncStateN, pipe } from '@blade/std-lib'
import { VirtualList } from '@root/shared/ui-kit'

import { FavoriteUnavailable } from '../../model/favorite-unavailable'

import { FavoritePairsListItem } from './favorite-pairs-list-item'
import { FavoritePairsListItemSkeleton } from './favorite-pairs-list-item-skeleton'

import type { FavoritePairsTableModel } from '../../atoms/reatom-favorite-pairs-table'
import type { Pair, PairModel } from '@root/entities/pair'

type FavoritePairsListProps = {
  model: FavoritePairsTableModel
  onPairClick: (pair: Pair.PairMinimal) => void
}

export const FavoritePairsList: React.FC<FavoritePairsListProps> = ({
  model,
}) => {
  const tableModel = model
  const [tableState] = useAtom(tableModel)
  const load = useAction(tableModel.load)
  const loadMore = useAction(tableModel.loadMore)
  const [loadMoreState] = useAtom(tableModel.loadMoreState)
  const [hasMoreItems] = useAtom(tableModel.hasMoreItems)

  const handleRetry = () => load()

  const itemsState = useMemo(() => {
    return AsyncStateN.map(tableState, (data) => {
      const pairs = data.data.map((it): PairModel | undefined =>
        it instanceof FavoriteUnavailable ? undefined : it,
      )

      return pipe(pairs, Array.compact)
    })
  }, [tableState])

  return (
    <List
      state={itemsState}
      getKey={(item) => item.address}
      renderItem={(pair, index) => (
        <FavoritePairsListItem
          key={pair.address}
          pair={pair}
          parity={index % 2 === 0 ? 'even' : 'odd'}
        />
      )}
      renderSkeleton={(index) => (
        <FavoritePairsListItemSkeleton.Item
          parity={index % 2 === 0 ? 'even' : 'odd'}
        />
      )}
      onLoadMore={loadMore}
      loadMoreState={loadMoreState}
      hasMore={hasMoreItems}
      onRetry={handleRetry}
      withFade={false}
    />
  )
}

const List = styled(VirtualList<PairModel>)`
  --virtual-list-padding-inline: 0;
  --virtual-list-gap: 0;
  --virtual-list-padding-block-start: 0;
  --virtual-list-padding-block-end: 120px;
`
